import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { registerSeller } from "../../../State/seller/sellerAuthSlice";
import { useAppDispatch } from "../../../State/Store";
import BecomeSellerFormStep1 from "./BecomeSellerFormStep1";
import BecomeSellerFormStep2 from "./BecomeSellerFormStep2";
import BecomeSellerFormStep3 from "./BecomeSellerFormStep3";
import BecomeSellerFormStep4 from "./BecomeSellerFormStep4";

const steps = [
  "Tax & Mobile",
  "Pickup Address",
  "Bank Details",
  "Account Information",
];

// Validation Schema covering all steps
const validationSchema = Yup.object().shape({
  mobile: Yup.string()
    .matches(/^[0-9+]{10,13}$/, "Enter a valid phone number")
    .required("Mobile number is required"),
  taxPin: Yup.string().required("Tax PIN / Registration ID is required"),
  pickupAddress: Yup.object().shape({
    name: Yup.string().required("Contact name is required"),
    mobile: Yup.string().required("Address mobile number is required"),
    address: Yup.string().required("Street address is required"),
    locality: Yup.string().required("Locality/Area is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("County / Region is required"),
    postalCode: Yup.string().required("Postal Code is required"),
  }),
  bankDetails: Yup.object().shape({
    accountNumber: Yup.string().required("Account number is required"),
    bankName: Yup.string().required("Bank name is required"),
    accountHolderName: Yup.string().required(
      "Account holder name is required"
    ),
    branchCode: Yup.string().required("Branch code is required"),
  }),
  businessDetails: Yup.object().shape({
    businessName: Yup.string().required("Business name is required"),
  }),
  sellerName: Yup.string().required("Full name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const SellerAccountForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({
    open: false,
    severity: "success",
    message: "",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      mobile: "",
      taxPin: "",
      pickupAddress: {
        name: "",
        mobile: "",
        address: "",
        locality: "",
        city: "",
        state: "",
        postalCode: "",
      },
      bankDetails: {
        accountNumber: "",
        bankName: "",
        accountHolderName: "",
        branchCode: "",
      },
      businessDetails: {
        businessName: "",
      },
      sellerName: "",
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const result = await dispatch(
          registerSeller({ sellerData: values, navigate })
        ).unwrap();

        setFeedback({
          open: true,
          severity: "success",
          message: "Seller account created successfully! Redirecting...",
        });
      } catch (err: any) {
        setFeedback({
          open: true,
          severity: "error",
          message:
            typeof err === "string"
              ? err
              : err?.message ||
                "Failed to register seller account. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      formik.handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCloseSnackbar = () => {
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  return (
    <Paper elevation={3} className="max-w-3xl mx-auto p-6 md:p-10 my-10">
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box className="mt-8 mb-6">
        <form onSubmit={formik.handleSubmit}>
          {activeStep === 0 && <BecomeSellerFormStep1 formik={formik} />}
          {activeStep === 1 && <BecomeSellerFormStep2 formik={formik} />}
          {activeStep === 2 && <BecomeSellerFormStep3 formik={formik} />}
          {activeStep === 3 && <BecomeSellerFormStep4 formik={formik} />}
        </form>
      </Box>

      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <Button
          onClick={handleBack}
          variant="outlined"
          disabled={activeStep === 0 || loading}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : activeStep === steps.length - 1 ? (
            "Create Account"
          ) : (
            "Next"
          )}
        </Button>
      </div>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={feedback.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SellerAccountForm;